import React, { useEffect, useMemo, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Button, Drawer, Flex, Input, List, Space, Typography } from 'antd';
import { toSearchText } from 'utils/text';

const { Text } = Typography;

const SelectMobile = ({
  value,
  onChange,
  mode,
  placeholder,
  options = [],
  disabled,
  loading,
  allowClear,
}) => {
  const [open, setOpen] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [draftValue, setDraftValue] = useState(value);

  const isMultiple = mode === 'multiple' || mode === 'tags';

  useEffect(() => {
    const timeout = setTimeout(() => {
      setKeyword(keywordInput);
    }, 200);

    return () => clearTimeout(timeout);
  }, [keywordInput]);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  const normalizedValue = useMemo(() => {
    if (isMultiple) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }

    return value ?? undefined;
  }, [isMultiple, value]);

  const filteredOptions = useMemo(() => {
    const normalizedKeyword = toSearchText(keyword.trim());

    if (!normalizedKeyword) {
      return options;
    }

    return (options || []).filter((item) => {
      const optionSearchText = item.searchText || toSearchText(String(item.label || ''));
      return optionSearchText.includes(normalizedKeyword);
    });
  }, [keyword, options]);

  const optionLabelMap = useMemo(() => {
    return new Map((options || []).map((item) => [item.value, item.label]));
  }, [options]);

  const selectedValueSet = useMemo(() => {
    return new Set(Array.isArray(draftValue) ? draftValue : []);
  }, [draftValue]);

  const selectedLabel = useMemo(() => {
    if (isMultiple) {
      const selectedValues = Array.isArray(normalizedValue) ? normalizedValue : [];

      if (!selectedValues.length) {
        return placeholder || 'Chọn dữ liệu';
      }

      const selectedLabels = selectedValues
        .map((item) => optionLabelMap.get(item))
        .filter(Boolean);

      if (selectedLabels.length <= 2) {
        return selectedLabels.join(', ');
      }

      return `Đã chọn ${selectedLabels.length} mục`;
    }

    if (normalizedValue === undefined || normalizedValue === null) {
      return placeholder || 'Chọn dữ liệu';
    }

    return optionLabelMap.get(normalizedValue) || placeholder || 'Chọn dữ liệu';
  }, [isMultiple, normalizedValue, optionLabelMap, placeholder]);

  const onOpen = () => {
    setDraftValue(isMultiple ? normalizedValue : value ?? undefined);
    setKeyword('');
    setKeywordInput('');
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setKeyword('');
    setKeywordInput('');
  };

  const triggerChange = (nextValue) => {
    const selectedOptions = isMultiple
      ? (options || []).filter((item) => (nextValue || []).includes(item.value))
      : (options || []).find((item) => item.value === nextValue);

    onChange?.(nextValue, selectedOptions);
  };

  const onToggleOption = (optionValue) => {
    if (!isMultiple) {
      triggerChange(optionValue);
      onClose();
      return;
    }

    setDraftValue((prevValue) => {
      const currentValues = Array.isArray(prevValue) ? prevValue : [];
      if (currentValues.includes(optionValue)) {
        return currentValues.filter((item) => item !== optionValue);
      }

      return [...currentValues, optionValue];
    });
  };

  const onApply = () => {
    triggerChange(draftValue || []);
    onClose();
  };

  const onClear = () => {
    const nextValue = isMultiple ? [] : undefined;
    setDraftValue(nextValue);
    if (!isMultiple) {
      triggerChange(nextValue);
      onClose();
    }
  };

  const isSelected = (optionValue) => (isMultiple ? selectedValueSet.has(optionValue) : draftValue === optionValue || normalizedValue === optionValue);

  return (
    <>
      <Button
        block
        onClick={onOpen}
        disabled={disabled}
        loading={loading}
        style={{ textAlign: 'left', overflow: 'hidden' }}
      >
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel}
        </span>
      </Button>

      <Drawer
        title={placeholder || 'Chọn dữ liệu'}
        placement='bottom'
        open={open}
        onClose={onClose}
        height='80vh'
        styles={{ body: { paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' } }}
      >
        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          <Input
            value={keywordInput}
            onChange={(event) => setKeywordInput(event.target.value)}
            placeholder='Tìm kiếm'
            allowClear
          />

          <div style={{ width: '100%', maxHeight: '52vh', overflowY: 'auto' }}>
            <List
              dataSource={filteredOptions}
              locale={{ emptyText: <Text type='secondary'>Không có dữ liệu phù hợp</Text> }}
              renderItem={(item) => (
                <List.Item
                  key={item.value}
                  onClick={() => !item.disabled && onToggleOption(item.value)}
                  style={{
                    cursor: item.disabled ? 'not-allowed' : 'pointer',
                    padding: '10px 4px',
                    opacity: item.disabled ? 0.5 : 1,
                  }}
                >
                  <Flex align='center' justify='space-between' gap={12} style={{ width: '100%' }}>
                    <Text style={{ flex: 1, minWidth: 0, textAlign: 'left' }} ellipsis={{ tooltip: typeof item.label === 'string' ? item.label : undefined }}>
                      {item.label}
                    </Text>
                    {isSelected(item.value) ? <CheckOutlined style={{ color: '#1677ff' }} /> : <span style={{ width: 14 }} />}
                  </Flex>
                </List.Item>
              )}
            />
          </div>

          <Flex gap={8}>
            <Button block onClick={onClear} disabled={!allowClear && !isMultiple ? true : isMultiple ? !(draftValue || []).length : draftValue === undefined || draftValue === null}>
              Xoá
            </Button>
            {isMultiple ? (
              <Button type='primary' block onClick={onApply}>
                Xong
              </Button>
            ) : (
              <Button type='primary' block onClick={onClose}>
                Đóng
              </Button>
            )}
          </Flex>
        </Space>
      </Drawer>
    </>
  );
};

export default SelectMobile;